/**
 * By length tab — one row per spreading marker: how long it was, how many layers were laid, how
 * much fabric it consumed and on which cloth.
 *
 * Replaces the legacy `sftsRpt/spreading/spreadingDetailByLength` screen. Every column comes off
 * the response envelope, so `report.yaml` stays the single description of them; the only thing
 * decided here is which of them are addends.
 */

import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';

import { integer } from '@/components/report/groupedColumns';

/**
 * The legacy range caps, `spreadingDetailByLength.js` ~45-48 (query) and ~106-109 (export). The
 * numbers are legacy's; nothing here is a new threshold.
 *
 * Legacy compared `date_start - date_end`, which is negative for any valid range, so neither cap
 * ever fired — see improvements.md. Applying them as written is a real narrowing of what a reader
 * could previously ask for, and is for the owner to confirm.
 */
export const QUERY_RANGE_DAYS = 30;
export const EXPORT_RANGE_DAYS = 90;

/** Days spanned by an inclusive `from`/`to` pair, or null while either end is unset. */
export function rangeDays(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (from === null || from === undefined || from === '') return null;
  if (to === null || to === undefined || to === '') return null;
  const days = differenceInCalendarDays(parseISO(to), parseISO(from));
  return Number.isNaN(days) ? null : days + 1;
}

/**
 * Whether the marker measures may be added down the column.
 *
 * `groupedColumns` totals every numeric column by default, which is right for a report whose
 * numbers are all quantities and wrong here: a marker's length, its planned layer count and its
 * perimeter are attributes of one marker, and a column of them summed is not a quantity of
 * anything. Bed and cloth numbers are identifiers that merely happen to be stored as integers.
 */
const SUMMABLE: ReadonlySet<string> = new Set([
  'finishqty',
  'completed_layer',
  'fabricconsum',
  'total_pieces',
]);

/** Strip the default `total: 'sum'` from every column that is not an addend, groups included. */
export function onlySummableTotals<TRow>(
  columns: readonly ColumnDef<TRow>[],
): ColumnDef<TRow>[] {
  return columns.map((column) => {
    const children = 'columns' in column ? column.columns : undefined;
    if (children !== undefined && children.length > 0) {
      return { ...column, columns: onlySummableTotals(children) };
    }
    if (column.id === undefined || SUMMABLE.has(column.id)) {
      return column;
    }
    const meta = { ...column.meta };
    delete meta.total;
    return { ...column, meta };
  });
}

/**
 * Totals formatter. Fabric consumption is the only non-integer addend and it is factory-scale, so
 * whole metres read better than a trailing `.00` nobody acts on.
 */
export function totalsFormat(value: number): React.ReactNode {
  return integer.format(Math.round(value));
}
